/**
 * Der Wochenplan (S62, Stufe 2; 25-zeitsteuerung.md § 7.3): folders – what stands on the net together,
 * a menu with its recommendations and drinks – and when which applies. The folder „Sonst“ is the
 * business's setting of before (id „“): it applies whenever no time of the plan does, so the page is
 * never empty (Z8). A time belongs to the day it begins, also over midnight; a day that is different
 * (an exception) replaces that day's plan. Overlaps are an error the app shows, never silently solved.
 */
import type { DrinksFrom } from "./card-web.ts";
import { atLocal, cleanWindow, dateAfter, localOf, weekdayOf } from "./hours.ts";
import type { Weekday, Window } from "./hours.ts";

/** What stands on the net together (S62 § 3, „Mappe“, Martin F5). */
export interface Folder {
  id: string;
  name: string;
  /** The menu of the QR-Code-Speisekarte in this folder. */
  menu: string;
  /** Its recommendations above (S37); „“ none. */
  recommendations: string;
  /** Its drinks (S59): „“ the menu's own, a fixed drinks menu (`drinksMenu`), KellerLog's, none. */
  drinksFrom: DrinksFrom;
  drinksMenu: string;
  /** Its colour in the bar of the day (0–3), the same everywhere. */
  tone: number;
}

/** „Sonst“ (F6): the business's setting of before, the folder for all times of no other. */
export const OTHERWISE = "";

/** A time of the plan: a folder at weekdays and a time of day. */
export interface Rule {
  folder: string;
  window: Window;
}

export interface Schedule {
  rules: Rule[];
  /** Ruhetage: the page shows „Sonst“ with „Heute geschlossen“. */
  closed: Weekday[];
}

/** A day that is different (Z6): closed, like another weekday, one folder all day, or its own times. */
export type Exception =
  | { date: string; kind: "closed" }
  | { date: string; kind: "like"; like: Weekday }
  | { date: string; kind: "only"; folder: string }
  | { date: string; kind: "own"; times: { folder: string; from: number; to: number }[] };

export const SCHEDULE_LIMITS = { folders: 8, rules: 60, exceptions: 60, name: 40 } as const;
export const EMPTY_SCHEDULE: Schedule = { rules: [], closed: [] };

/** A piece of a day: a folder from a minute to a minute; over midnight `to` ≤ `from`. */
export interface DayTime {
  folder: string;
  from: number;
  to: number;
}

/** The times of a local date, by the plan or by its exception; and whether it is closed. */
export function dayTimes(schedule: Schedule, exceptions: readonly Exception[], date: string): { times: DayTime[]; closed: boolean; exception?: Exception } {
  const day = weekdayOf(date);
  const ex = exceptions.find((e) => e.date === date);
  const weekly = (d: Weekday) => schedule.rules.filter((r) => r.window.days.includes(d)).map((r) => ({ folder: r.folder, from: r.window.from, to: r.window.to }));
  if (ex) {
    if (ex.kind === "closed") return { times: [], closed: true, exception: ex };
    if (ex.kind === "like") return { times: weekly(ex.like), closed: schedule.closed.includes(ex.like), exception: ex };
    if (ex.kind === "only") return { times: [{ folder: ex.folder, from: 0, to: 1440 }], closed: false, exception: ex };
    return { times: ex.times.map((t) => ({ ...t })), closed: false, exception: ex };
  }
  return { times: weekly(day), closed: schedule.closed.includes(day) };
}

const covers = (t: DayTime, minute: number) => (t.from < t.to ? minute >= t.from && minute < t.to : minute >= t.from);
const tail = (t: DayTime, minute: number) => t.from >= t.to && minute < t.to;

/** Which folder applies at a moment, and whether the business is closed that day; „“ is „Sonst“. */
export function folderNow(schedule: Schedule, exceptions: readonly Exception[], at: number): { folder: string; closed: boolean } {
  const l = localOf(at);
  const today = dayTimes(schedule, exceptions, l.date);
  const hit = today.times.find((t) => covers(t, l.minute)) ?? dayTimes(schedule, exceptions, dateAfter(l.date, -1)).times.find((t) => tail(t, l.minute));
  return { folder: hit?.folder ?? OTHERWISE, closed: today.closed && !hit };
}

/** The moments where a time of the plan begins or ends, from a day before to `days` after. */
function edges(schedule: Schedule, exceptions: readonly Exception[], from: number, days: number): number[] {
  const start = localOf(from).date;
  const out: number[] = [];
  for (let k = -1; k <= days; k++) {
    const date = dateAfter(start, k);
    out.push(atLocal(date, 0));
    for (const t of dayTimes(schedule, exceptions, date).times) {
      out.push(atLocal(date, t.from));
      out.push(t.from < t.to ? atLocal(date, t.to) : atLocal(dateAfter(date, 1), t.to));
    }
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * What applies at a moment (§ 7.3 `folderAt`): the folder, until when, and what comes next; within a
 * week and a day, else no next.
 */
export function folderAt(schedule: Schedule, exceptions: readonly Exception[], at: number): { folder: string; closed: boolean; until: number | null; next: { at: number; folder: string } | null } {
  const now = folderNow(schedule, exceptions, at);
  for (const t of edges(schedule, exceptions, at, 8)) {
    if (t <= at) continue;
    const then = folderNow(schedule, exceptions, t);
    if (then.folder !== now.folder) return { ...now, until: t, next: { at: t, folder: then.folder } };
  }
  return { ...now, until: null, next: null };
}

/** The pieces of a local day for its bar (§ 6.1): folders from minute to minute, „Sonst“ between them. */
export function dayPlan(schedule: Schedule, exceptions: readonly Exception[], date: string): { pieces: DayTime[]; closed: boolean; exception?: Exception } {
  const today = dayTimes(schedule, exceptions, date);
  const before = dayTimes(schedule, exceptions, dateAfter(date, -1)).times.filter((t) => t.from >= t.to && t.to > 0).map((t) => ({ folder: t.folder, from: 0, to: t.to }));
  const pieces = [...before, ...today.times.map((t) => ({ folder: t.folder, from: t.from, to: t.from < t.to ? t.to : 1440 }))].sort((a, b) => a.from - b.from);
  return { pieces, closed: today.closed, ...(today.exception ? { exception: today.exception } : {}) };
}

/** What is wrong with a plan (§ 7.3 `checkSchedule`): two times of a day that overlap, a folder that is not there. */
export function checkSchedule(schedule: Schedule, folders: readonly Pick<Folder, "id" | "name">[]): string[] {
  const names = new Map([[OTHERWISE, "Sonst"], ...folders.map((f): [string, string] => [f.id, f.name])]);
  const out: string[] = [];
  const dayNames = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
  for (const r of schedule.rules) if (!names.has(r.folder)) out.push("Eine Zeit nennt eine Mappe, die es nicht mehr gibt.");
  for (let d = 0 as Weekday; d <= 6; d = (d + 1) as Weekday) {
    const spans = schedule.rules.filter((r) => r.window.days.includes(d)).map((r) => ({ name: names.get(r.folder) ?? "?", a: r.window.from, b: r.window.from < r.window.to ? r.window.to : 1440 + r.window.to }));
    for (let i = 0; i < spans.length; i++)
      for (let j = i + 1; j < spans.length; j++) if (spans[i]!.a < spans[j]!.b && spans[j]!.a < spans[i]!.b) out.push(`${dayNames[d]}: „${spans[i]!.name}“ und „${spans[j]!.name}“ überschneiden sich.`);
  }
  return [...new Set(out)];
}

/** A plan as the app sends it, cleaned: rules with a window and a folder, closed days 0–6. */
export function cleanSchedule(v: unknown): Schedule {
  const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const rules = (Array.isArray(o.rules) ? o.rules : []).slice(0, SCHEDULE_LIMITS.rules).flatMap((r): Rule[] => {
    const x = r && typeof r === "object" ? (r as Record<string, unknown>) : {};
    const window = cleanWindow(x.window);
    return window && typeof x.folder === "string" && x.folder.length <= 64 ? [{ folder: x.folder, window }] : [];
  });
  const closed = (Array.isArray(o.closed) ? o.closed : []).filter((d): d is Weekday => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6);
  return { rules, closed: [...new Set(closed)].sort((a, b) => a - b) };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Exceptions as the app sends them, cleaned; one per date, those in the past (before `today`) gone. */
export function cleanExceptions(v: unknown, today: string): Exception[] {
  const seen = new Set<string>();
  return (Array.isArray(v) ? v : []).slice(0, SCHEDULE_LIMITS.exceptions).flatMap((r): Exception[] => {
    const x = r && typeof r === "object" ? (r as Record<string, unknown>) : {};
    const date = typeof x.date === "string" && DATE.test(x.date) ? x.date : "";
    if (!date || date < today || seen.has(date)) return [];
    seen.add(date);
    if (x.kind === "closed") return [{ date, kind: "closed" }];
    if (x.kind === "like" && Number.isInteger(x.like) && (x.like as number) >= 0 && (x.like as number) <= 6) return [{ date, kind: "like", like: x.like as Weekday }];
    if (x.kind === "only" && typeof x.folder === "string") return [{ date, kind: "only", folder: x.folder }];
    if (x.kind === "own" && Array.isArray(x.times)) {
      const times = x.times.slice(0, 6).flatMap((t) => {
        const w = cleanWindow({ ...(t as object), days: [0] });
        const f = (t as Record<string, unknown>).folder;
        return w && typeof f === "string" ? [{ folder: f, from: w.from, to: w.to }] : [];
      });
      return [{ date, kind: "own", times }];
    }
    return [];
  }).sort((a, b) => a.date.localeCompare(b.date));
}

/** A day of the plan in words (Bild 8): „11:30–14:30 Mittag · 17:00–23:00 Abend · sonst Abend“. */
export function dayWords(times: readonly DayTime[], name: (folder: string) => string, closed = false): string {
  const clock = (m: number) => `${String(Math.floor((m % 1440) / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const parts = [...times].sort((a, b) => a.from - b.from).map((t) => `${clock(t.from)}–${clock(t.to)} ${name(t.folder)}`);
  return [...parts, closed ? `Ruhetag, sonst ${name(OTHERWISE)}` : `sonst ${name(OTHERWISE)}`].join(" · ");
}

